import type { CurrencyTypeConfig } from "@/models/custom-fields";

/** The currency a field starts on when its `type_config` names none. */
export const DEFAULT_CURRENCY = "EUR";

/** Digits of an amount at most, so the integer in the minor unit stays below `Number.MAX_SAFE_INTEGER`. */
const MAX_DIGITS = 15;

/** The locale whose number format a currency is written in, e.g. Euro as `123.456.789,01 €` and US dollar as `$123,456,789.01`. */
const CURRENCY_LOCALES: Record<string, string> = {
  EUR: "de-DE",
  USD: "en-US",
  GBP: "en-GB",
  CHF: "de-CH",
  JPY: "ja-JP",
  CAD: "en-CA",
  AUD: "en-AU",
  CNY: "zh-CN",
  KRW: "ko-KR",
  INR: "en-IN",
  SEK: "sv-SE",
  NOK: "nb-NO",
  DKK: "da-DK",
  PLN: "pl-PL",
  CZK: "cs-CZ",
};

const COMMON_CURRENCIES = ["EUR", "USD", "GBP", "CHF", "JPY"];

export function localeForCurrency(code: string): string {
  const fallback = typeof navigator !== "undefined" && navigator.language ? navigator.language : "en-US";
  return CURRENCY_LOCALES[code] ?? fallback;
}

/** The number of fraction digits of the currency (ISO 4217 exponent), which is the size of its minor unit: 2 for EUR, 0 for JPY, 3 for KWD. */
export function fractionDigits(code: string): number {
  try {
    return new Intl.NumberFormat("en", { style: "currency", currency: code }).resolvedOptions().maximumFractionDigits ?? 2;
  } catch {
    return 2;
  }
}

export type Separators = { group: string; decimal: string };

export function separatorsFor(code: string): Separators {
  const parts = new Intl.NumberFormat(localeForCurrency(code), { minimumFractionDigits: 1 }).formatToParts(1234567.5);
  return {
    group: parts.find((part) => part.type === "group")?.value ?? ",",
    decimal: parts.find((part) => part.type === "decimal")?.value ?? ".",
  };
}

export type ResolvedCurrencyConfig = { currency: string; fixed: boolean };

/** The settings of a currency field with the defaults filled in, `null` (a field from before) selects among all currencies. */
export function resolveCurrencyConfig(typeConfig: unknown): ResolvedCurrencyConfig {
  const currency = (typeConfig as Partial<CurrencyTypeConfig> | null | undefined)?.properties?.currency;
  return { currency: currency?.const ?? currency?.default ?? DEFAULT_CURRENCY, fixed: currency?.const !== undefined };
}

export const defaultCurrencyConfig = (): ResolvedCurrencyConfig => ({ currency: DEFAULT_CURRENCY, fixed: false });

/** The `type_config` to send for the settings: `const` for a fixed currency, `default` for the one the selector starts on. */
export function toCurrencyConfig(config: ResolvedCurrencyConfig): CurrencyTypeConfig {
  return { properties: { currency: config.fixed ? { const: config.currency } : { default: config.currency } } };
}

export type CurrencyOption = { code: string; name: string };

let optionsCache: CurrencyOption[] | null = null;

/** All currencies the browser knows, the common ones first, then by name. */
export function currencyOptions(): CurrencyOption[] {
  if (optionsCache) return optionsCache;
  const codes = (Intl as unknown as { supportedValuesOf?: (key: string) => string[] }).supportedValuesOf?.("currency") ?? COMMON_CURRENCIES;
  const names = new Intl.DisplayNames([typeof navigator !== "undefined" ? navigator.language : "en"], { type: "currency" });
  const options = codes.map((code) => ({ code, name: names.of(code) ?? code }));
  const rank = (code: string) => (COMMON_CURRENCIES.includes(code) ? COMMON_CURRENCIES.indexOf(code) : COMMON_CURRENCIES.length);
  options.sort((a, b) => rank(a.code) - rank(b.code) || a.name.localeCompare(b.name));
  optionsCache = options;
  return options;
}

/** An amount in the minor unit of its currency, written like the currency's locale does: `1.234,50 €`. */
export function formatAmount(minorUnits: number, code: string): string {
  return new Intl.NumberFormat(localeForCurrency(code), { style: "currency", currency: code }).format(
    minorUnits / 10 ** fractionDigits(code),
  );
}

function group(digits: string, separator: string): string {
  return digits.replace(/\B(?=(\d{3})+(?!\d))/g, separator);
}

/** The number as it is shown in the input: grouped integer part, all fraction digits of the currency (`1.234,50`). */
export function amountToText(minorUnits: number, code: string): string {
  const { group: groupSeparator, decimal } = separatorsFor(code);
  const digits = fractionDigits(code);
  const sign = minorUnits < 0 ? "-" : "";
  const padded = String(Math.abs(Math.round(minorUnits))).padStart(digits + 1, "0");
  const integer = padded.slice(0, padded.length - digits);
  return `${sign}${group(integer, groupSeparator)}${digits ? decimal + padded.slice(padded.length - digits) : ""}`;
}

/** The amount in the minor unit for the text of the input, `null` if it has no digit. A missing fraction counts as zero. */
export function textToAmount(text: string, code: string): number | null {
  const { decimal } = separatorsFor(code);
  const digits = fractionDigits(code);
  const [integer = "", fraction = ""] = text.replaceAll("-", "").split(decimal);
  const cleanInteger = integer.replace(/\D/g, "");
  const cleanFraction = fraction.replace(/\D/g, "");
  if (!cleanInteger && !cleanFraction) return null;
  const amount = Number((cleanInteger || "0") + cleanFraction.slice(0, digits).padEnd(digits, "0"));
  return text.trim().startsWith("-") ? -amount : amount;
}

/** The same number in the minor unit of another currency (no exchange rate): 123.45 EUR is 123 JPY, fractions that do not fit are cut off. */
export function convertMinorUnits(minorUnits: number, from: string, to: string): number {
  const shift = fractionDigits(to) - fractionDigits(from);
  return shift >= 0 ? minorUnits * 10 ** shift : Math.trunc(minorUnits / 10 ** -shift);
}

export type TypedText = { text: string; caret: number };

type Token = { char: string; raw: number };

/**
 * Cleans up what was typed: keeps digits, a leading minus and the decimal separator of the currency (once, and only if the
 * currency has fractions), drops everything else (so the group separators are re-created, and a `.` typed in Euro, where the dot
 * groups, does nothing), limits the fraction digits, groups the integer part and moves the caret so it stays behind the same
 * characters as before.
 */
export function formatWhileTyping(raw: string, code: string, caret: number = raw.length): TypedText {
  const { group: groupSeparator, decimal } = separatorsFor(code);
  const maxFraction = fractionDigits(code);

  let tokens: Token[] = [];
  let hasDecimal = false;
  let digitCount = 0;
  let fractionCount = 0;
  [...raw].forEach((char, rawIndex) => {
    if (char === "-" && tokens.length === 0) {
      tokens.push({ char, raw: rawIndex });
    } else if (/\d/.test(char) && digitCount < MAX_DIGITS && (!hasDecimal || fractionCount < maxFraction)) {
      tokens.push({ char, raw: rawIndex });
      digitCount += 1;
      if (hasDecimal) fractionCount += 1;
    } else if (char === decimal && maxFraction > 0 && !hasDecimal) {
      tokens.push({ char, raw: rawIndex });
      hasDecimal = true;
    }
  });

  // Leading zeros of the integer part go: "007" is "7", but "0" and "0," stay.
  const start = tokens[0]?.char === "-" ? 1 : 0;
  let zeros = 0;
  while (tokens[start + zeros]?.char === "0" && /\d/.test(tokens[start + zeros + 1]?.char ?? "")) zeros += 1;
  tokens = [...tokens.slice(0, start), ...tokens.slice(start + zeros)];

  const before = tokens.filter((token) => token.raw < caret).length;
  const integerLength = tokens.slice(start).findIndex((token) => token.char === decimal);
  const integerDigits = integerLength === -1 ? tokens.length - start : integerLength;

  let text = "";
  let newCaret = 0;
  tokens.forEach((token, index) => {
    const position = index - start;
    if (position > 0 && position < integerDigits && (integerDigits - position) % 3 === 0) text += groupSeparator;
    text += token.char;
    if (index < before) newCaret = text.length;
  });
  return { text, caret: before === 0 ? 0 : newCaret };
}

/**
 * Backspace behind a group separator: remove the digit before it as well, since removing only the separator would bring it
 * right back. `null` if the character before the caret is no separator (the normal Backspace does it, the input event then
 * runs `formatWhileTyping`).
 */
export function deleteBackward(text: string, caret: number, code: string): TypedText | null {
  const { group: groupSeparator } = separatorsFor(code);
  if (caret < 2 || text[caret - 1] !== groupSeparator) return null;
  return formatWhileTyping(text.slice(0, caret - 2) + text.slice(caret), code, caret - 2);
}
